import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import MDBox from "ui/MDBox";
import MDTypography from "ui/MDTypography";
import MDInput from "ui/MDInput";
import MDButton from "ui/MDButton";
import BasicLayoutLanding from "layouts/authentication/components/BasicLayoutLanding";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import AuthService from "services/auth-service";
import { AuthContext } from "context";
import Notification from "examples/Notifications";

function Login() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [inputs, setInputs] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);

  // ----------------------------------
  // Input Change
  // ----------------------------------
  const changeHandler = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  // ----------------------------------
  // Validation
  // ----------------------------------
  const validateForm = () => {
    const { email, password } = inputs;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim() && !password.trim()) {
      setNotification({ type: "error", message: "Please fill in all fields." });
      return false;
    }

    if (!email.trim()) {
      setNotification({ type: "error", message: "Email is required." });
      return false;
    }

    if (!emailRegex.test(email)) {
      setNotification({ type: "error", message: "Invalid email format." });
      return false;
    }

    if (!password.trim()) {
      setNotification({ type: "error", message: "Password is required." });
      return false;
    }

    if (password.trim().length < 8) {
      setNotification({
        type: "error",
        message: "Password must be at least 8 characters long.",
      });
      return false;
    }

    return true;
  };

  // ----------------------------------
  // Submit Handler
  // ----------------------------------
  const submitHandler = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const response = await AuthService.login(inputs);

      if (response && response.token && response.user) {
        // ✅ Login Success
        localStorage.setItem("authToken", JSON.stringify(response.token));
        localStorage.setItem("user", JSON.stringify(response.user));

        authContext.login({ token: response.token, user: response.user });
        authContext.setUser(response.user);

        setNotification({
          type: "success",
          message: "Login successful! Redirecting...",
        });

        setTimeout(() => navigate("/dashboard"), 1500);
      } else {
        // ❌ Invalid credentials
        setNotification({
          type: "error",
          message: "Invalid email or password. Please try again.",
        });
        setInputs({ email: "", password: "" });
      }
    } catch (error) {
      console.error("Login error:", error);

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again.";

      setNotification({ type: "error", message });
      setInputs({ email: "", password: "" });
    }

    finally {
      setLoading(false);
    }
  };

  // ----------------------------------
  // UI
  // ----------------------------------
  return (
    <BasicLayoutLanding image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography
            className="sign-in-class"
            variant="h4"
            fontWeight="medium"
            color="white"
            mt={1}
          >
            Sign in
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                name="email"
                value={inputs.email}
                onChange={changeHandler}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                name="password"
                value={inputs.password}
                onChange={changeHandler}
              />
            </MDBox>

            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                type="submit"
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign in"}
              </MDButton>
            </MDBox>

            <MDBox mt={3} textAlign="center">
              <MDTypography variant="button" color="text" fontWeight="regular">
                Don’t have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/auth/register"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  sx={{ textDecoration: "none" }}
                >
                  Sign up
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      <Notification notification={notification} setNotification={setNotification} />
    </BasicLayoutLanding>
  );
}

export default Login;
